import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

/**
 * API sometimes sends short labels (EET, EST) in `details.timezone` instead of
 * an IANA name. Used as an offset fallback when Intl doesn't know the label.
 */
export const BOOKING_TIMEZONE_LABEL_MAP: Record<string, string> = {
    EET: '+02:00',
    EST: '-05:00'
};

const WALL_CLOCK_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

const normalizeAsWallClock = (value: string) =>
    value
        .replace(/\.\d{3}/, '')
        .replace(/Z$/, '')
        .replace(/[+-]\d{2}:\d{2}$/, '');

/** `Z`, `+03:00` or `+0300` at the end of an ISO datetime. */
const OFFSET_SUFFIX = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Does this value pin its own instant, or does it need a convention to be read?
 *
 * Everything the BE stores today is offset-less, so what a bare
 * "2026-07-12T07:00:00" means is pure convention — and the FE and BE have never
 * agreed on it (we say UTC, `parse_booking_datetime` says listing-local). A
 * value that carries its offset needs no convention at all, which is the point
 * of the `2026-08-10T09:00:00+03:00` contract in hotdesk-ui#629 option B.
 *
 * Only strings can be self-describing — see `parseBookingUtcInstant` for why a
 * `Date` cannot.
 */
const isSelfDescribing = (date: Date | string): boolean =>
    typeof date === 'string' && OFFSET_SUFFIX.test(date.trim());

/**
 * The true UTC instant behind a stored booking datetime.
 *
 * Two shapes arrive here:
 *
 * - **Offset-bearing** ("…T09:00:00+03:00", "…Z") — trust it verbatim.
 * - **Offset-less** ("2026-07-12T07:00:00") — everything stored to date. Read
 *   as UTC, which is the convention this module has always applied.
 *
 * The `Date` branch cannot honour an offset. That *was* an open hazard for
 * option B and is now closed: `hotdesk_typescript_api_client!190` retypes
 * `BookingQuotation` and `BookingDetails.startDateTime`/`endDateTime` to
 * `string`, so the quote and read sides both stay self-describing. The branch
 * is kept for values that reach here as `Date` anyway — `rehydrateBookingDatesForUpdate`
 * produces them, and JSON round-trips can too. The hazard it used to describe:
 *
 * - `new Date("…T07:00:00")` parses as *browser-local*, so the Date's local
 *   components (not its instant) hold the stored digits — which is what the
 *   round-trip below recovers.
 * - `new Date("…T09:00:00+03:00")` parses correctly, so those same local
 *   components become a browser-dependent rendering and the round-trip
 *   produces garbage that differs per user.
 *
 * The two are indistinguishable once `new Date` has run — which is why the
 * retype above had to land before the BE starts emitting offsets, and why
 * nothing here may coerce a booking datetime to `Date` on the way in.
 */
export const parseBookingUtcInstant = (date: Date | string): Dayjs => {
    if (typeof date === 'string') {
        if (OFFSET_SUFFIX.test(date.trim())) return dayjs(date).utc();
        return dayjs.utc(normalizeAsWallClock(date));
    }
    return dayjs.utc(dayjs(date).format(WALL_CLOCK_FORMAT));
};

/**
 * `JSON.stringify` turns a Date into `.toISOString()`, which on the misparsed
 * Dates above bakes in an instant shifted by the machine's UTC offset — the
 * value then differs between server, dev machine, and user browser. This
 * round-trip emits the UTC wall clock (via `this[key]`, since the replacer
 * only sees the already-stringified value) so downstream formatting stays
 * machine-independent.
 */
const bookingJsonReplacer = function (this: Record<string, unknown>, key: string, value: unknown) {
    const raw = this[key];
    return raw instanceof Date ? dayjs(raw).format(WALL_CLOCK_FORMAT) : value;
};

export const serializeBookingDates = <T>(data: T): T =>
    JSON.parse(JSON.stringify(data, bookingJsonReplacer));

/**
 * A `Date` holding the value's **true instant**, for the fields the SDK still
 * types as `Date`.
 *
 * Not `new Date(value)`. These arrive offset-less carrying UTC digits, and
 * `new Date("2026-08-20T06:00:00")` parses that as *browser-local* — so the
 * instant, and therefore the `.toISOString()` the serializer sends back, comes
 * out shifted by the viewer's own offset. `parseBookingUtcInstant` applies the
 * documented reading instead, and an offset-bearing value is still honoured.
 */
const toDate = (value: unknown): Date | undefined => {
    if (value instanceof Date) return value;
    if (typeof value === 'string' && value) {
        const parsed = parseBookingUtcInstant(value);
        return parsed.isValid() ? parsed.toDate() : undefined;
    }
    return undefined;
};

type BookingDateFields = {
    createdAt?: unknown;
    updatedAt?: unknown;
    details?: {
        // `startDateTime`/`endDateTime` are intentionally absent — see the
        // warning on `rehydrateBookingDatesForUpdate`. They pass through in the
        // spread as the strings the BE sent.
        cancellationThreshold?: unknown;
        expirationThreshold?: unknown;
    } | null;
};

/**
 * Raw booking reads (`getBookingByIdRaw`) and JSON round-trips leave date
 * fields as strings. The generated client's ObjectSerializer calls
 * `.toISOString()` on Date-typed attributes during `bookingUpdate`, so convert
 * those back to `Date` before any cancel/update PUT.
 *
 * ⚠️ `startDateTime`/`endDateTime` are deliberately **not** touched. As of
 * `hotdesk_typescript_api_client!190` the SDK types them as `string`, and
 * `ObjectSerializer.serialize` passes primitives through untouched — so a
 * `Date` parked on them survives to `JSON.stringify`, which emits
 * `.toISOString()`. Combined with `new Date` reading an offset-less value as
 * browser-local, cancelling a booking used to rewrite its own start and end by
 * the difference between the viewer's offset and the listing's: the same
 * cancel from London and from Cairo stored two different windows, and
 * `Booking.save()` then re-derived the indexed columns from them. A booking
 * datetime must reach the wire exactly as the string the BE sent.
 */
export const rehydrateBookingDatesForUpdate = <T extends BookingDateFields>(
    booking: T
): T => {
    const details = (booking.details ?? {}) as NonNullable<T['details']> &
        Record<string, unknown>;
    return {
        ...booking,
        createdAt: toDate(booking.createdAt) ?? booking.createdAt,
        updatedAt: toDate(booking.updatedAt) ?? booking.updatedAt,
        details: {
            ...details,
            cancellationThreshold:
                toDate(details.cancellationThreshold) ??
                details.cancellationThreshold,
            expirationThreshold:
                toDate(details.expirationThreshold) ??
                details.expirationThreshold
        }
    };
};

/**
 * The **true instant** a booking datetime refers to.
 *
 * This is the one place that knows how to read a stored value, and everything
 * else should go through it — formatting (`toBookingZonedTime`), calendar
 * export, and elapsed-time maths all need the same answer, and three call
 * sites each remembering the rule is how they drift apart.
 *
 * Two cases:
 *
 * 1. **Offset-bearing** ("…+03:00", "…Z") — already pins its instant. Trust it.
 *    This is what the quote and create responses return.
 * 2. **Offset-less** ("2026-08-13T08:30:00") — the digits are the *listing's*
 *    wall clock, so the instant is those digits read **in** the listing's zone:
 *    `09:00` on a +3 listing is 06:00Z, not 09:00Z.
 *
 * Case 2 exists because `ReadOnlyBookingSerializer` runs
 * `Date_Time_Formatter_Filter` over `start_date_time`/`end_date_time`, and
 * `datetime_to_str` formats with `DATE_TIME_FORMAT = "%Y-%m-%dT%H:%M:%S"` —
 * no offset. So the booking-details response strips the offset the client sent
 * (other endpoints don't, which is why they come back self-describing).
 *
 * What survives that strip is listing-local for **every** unit — but only as
 * of `hotdesk_backend` `b1c738e7`. Before it, `PricingEngine.start_date_time`
 * returned `hour` untouched (the UTC instant DRF normalised the payload to),
 * and native create echoes the quote envelope verbatim, so native `hour` rows
 * carried UTC digits while provider ones carried local. `b1c738e7` re-zones
 * `hour` like the other units, collapsing the two conventions at the source.
 *
 * There is deliberately no unit special case here: gating this on
 * `day`/`month`/`annual` left `hour` bookings read as UTC and displayed a full
 * offset late (14:00 in Cairo rendered as 17:00). A provenance gate is not the
 * answer either — it was tried backend-side in `46d5481d` and reverted in
 * `010baaa6`, because bare digits are overwhelmingly mobile-written and local.
 *
 * Known residual, not fixable from here: native `hour` rows written *before*
 * that backend deploy still hold UTC digits and are indistinguishable from
 * local ones, so they render one offset early. Historical only; not backfilled.
 *
 * Case 2 disappears entirely once the BE stops stripping the offset (#629
 * option B) — it is a reading of offset-less values, not a permanent rule.
 *
 * Without a timezone it degrades to reading the digits as UTC, which is the
 * behaviour this module has always had.
 */
export const resolveBookingInstant = (
    date: Date | string,
    timezone?: string | null
): Dayjs => {
    const instant = parseBookingUtcInstant(date);
    if (isSelfDescribing(date)) return instant;
    if (timezone) {
        const wallClock = instant.format(WALL_CLOCK_FORMAT);
        try {
            const zoned = dayjs.tz(wallClock, timezone);
            if (zoned.isValid()) return zoned;
        } catch {
            const offset = BOOKING_TIMEZONE_LABEL_MAP[timezone];
            if (offset) return dayjs(`${wallClock}${offset}`);
        }
    }
    return instant;
};

/**
 * The UTC instant shifted into the booking's timezone (`details.timezone`,
 * normally IANA e.g. "Africa/Cairo"). Falls back to the offset map for short
 * labels Intl can't resolve, and to plain UTC when no timezone is given.
 *
 * Built on `resolveBookingInstant`, so the "what does this value mean" rule
 * lives in one place rather than at every call site.
 */
export const toBookingZonedTime = (
    date: Date | string,
    timezone?: string | null
): Dayjs => {
    const instant = resolveBookingInstant(date, timezone);
    if (timezone) {
        try {
            return instant.tz(timezone);
        } catch {
            const offset = BOOKING_TIMEZONE_LABEL_MAP[timezone];
            if (offset) return instant.utcOffset(offset);
        }
    }
    return instant;
};

/**
 * Format a booking instant for display in the listing's timezone. Without a
 * timezone this degrades to the old behavior (UTC wall clock), which keeps
 * My Bookings and Booking Details consistent on machines in any zone.
 */
export const formatBookingDateTime = (
    date?: Date | string | null,
    pattern = 'MMM D, YYYY h:mm a',
    timezone?: string | null
) => {
    if (!date) return '';
    return toBookingZonedTime(date, timezone).format(pattern);
};

/**
 * The literal date and time parts of a busy segment from
 * `GET /listing/{id}/availability/`.
 *
 * These arrive offset-less ("2026-07-20T14:00:00") and are the **listing's own
 * wall clock**, not UTC. Both branches of the endpoint agree on that:
 *
 * - **provider** — `_compute_provider_busy_segments` asks the provider for its
 *   ranges with `in_location_time=True`, because "a calendar date only means
 *   anything in the desk's own timezone".
 * - **native** — the buckets are built through `in_listing_wall_clock`, which
 *   exists so that "every storage era agrees" on the local calendar day.
 *
 * So the digits are already the answer, and any clock we parse them through can
 * only corrupt them. Two wrong readings were live at once before this helper:
 * `dayjs(value)` reads a naive string in the BROWSER's zone, and
 * `dayjs.utc(value).tz(listingTz)` shifts it by the listing's offset a second
 * time. They disagreed with each other, and each greyed out the wrong dates or
 * the wrong hour slots depending on where the viewer was sitting.
 *
 * ⚠️ The SDK's own `DateRange` docstring still claims these values "are UTC"
 * and tells consumers to `dayjs.utc(...)` them. That comment is wrong — it
 * predates the backend settling the convention. Do not reinstate it here.
 *
 * A `Date` is tolerated only because `rangeStart` is defensively typed; the SDK
 * keeps these as raw strings precisely so no runtime zone can touch them, and
 * the round-trip below recovers the digits a local-zone `new Date` would hold.
 */
export const splitAvailabilityWallClock = (
    value: string | Date
): { date: string; time: string } => {
    const raw =
        typeof value === 'string'
            ? value.trim()
            : dayjs(value).format(WALL_CLOCK_FORMAT);
    return { date: raw.slice(0, 10), time: raw.slice(11, 19) };
};

/** `YYYY-MM-DD` if this value starts with a well-formed date, else `null`. */
export const availabilityDateKey = (value: string | Date): string | null => {
    const { date } = splitAvailabilityWallClock(value);
    return /^\d{4}-\d\d-\d\d$/.test(date) ? date : null;
};
